import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { emailSchema } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';

export const Route = createFileRoute('/signup')({
  component: SignUpPage,
});

// A2 invitation codes are 8-char uppercase alphanumerics (see
// 20260520203720_a2_invitations_role_provisioning.sql — invitations.code
// CHECK ~ '^[A-Z0-9]{8}$'). The signup trigger consumes
// raw_user_meta_data.invitation_code; WITHOUT a valid code the new user gets
// no auth_user_roles row → RLS denies all data. We therefore REQUIRE the code
// here so self-service signup never creates a roleless, locked-out account.
const signupSchema = z
  .object({
    email: emailSchema,
    password: z.string().min(8, 'Use at least 8 characters'),
    confirmPassword: z.string(),
    invitationCode: z
      .string()
      .trim()
      .transform((v) => v.toUpperCase())
      .pipe(
        z
          .string()
          .regex(
            /^[A-Z0-9]{8}$/,
            'Join codes are 8 letters and numbers — check it with your studio',
          ),
      ),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
type SignUpInput = z.infer<typeof signupSchema>;

function SignUpPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent' | 'error'>('idle');
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SignUpInput>({
    resolver: zodResolver(signupSchema),
    mode: 'onBlur',
  });

  async function onSubmit(values: SignUpInput) {
    setStatus('submitting');
    setServerError(null);
    const { data, error } = await supabase.auth.signUp({
      email: values.email,
      password: values.password,
      options: {
        // The A2 trigger reads this off raw_user_meta_data on insert and
        // calls consume_invitation(code, user_id) to provision role + family.
        data: { invitation_code: values.invitationCode },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[signup] signUp failed', { code: error.code, status: error.status });
      setStatus('error');
      if (error.code === 'user_already_exists' || /already registered/i.test(error.message ?? '')) {
        setServerError('An account with this email already exists. Try signing in instead.');
      } else {
        // A bad/expired/used invite code surfaces as a trigger exception
        // ("Database error saving new user"). We can't reliably distinguish it
        // from other server faults, so guide the user to the most likely cause.
        setServerError(
          "We couldn't create your account. Your join code may be invalid, expired, or already used — double-check it with your studio.",
        );
      }
      return;
    }

    if (data.session) {
      // Email confirmation disabled on the project → signed in immediately.
      await navigate({ to: '/' });
      return;
    }
    // Confirmation required: no session yet. Tell them to check email.
    setStatus('sent');
  }

  return (
    <main
      id="main"
      className="grid min-h-dvh grid-cols-1 lg:grid-cols-[1.1fr_1fr]"
      data-testid="signup-page"
    >
      <aside
        aria-hidden="true"
        className="relative hidden overflow-hidden bg-[var(--color-surface-sunken)] lg:block"
      >
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <p className="font-[var(--font-display)] tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-lg)] uppercase">
            Pirouette
          </p>
          <div className="max-w-xl">
            <h1 className="leading-[var(--leading-tight)] font-[var(--font-display)] tracking-[var(--tracking-tight)] text-[var(--color-ink)] text-[var(--text-display)]">
              Your season,
              <br />
              <em className="text-[var(--color-accent)] not-italic">in one place.</em>
            </h1>
            <p className="mt-6 max-w-md leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)] text-[var(--text-lg)]">
              Schedules, attendance, recital tickets, and tuition — created from the join code your
              studio sent you.
            </p>
          </div>
          <p className="text-[var(--color-ink-subtle)] text-[var(--text-sm)]">
            Designed for one studio. Built to stay out of the way.
          </p>
        </div>
      </aside>

      <section className="flex items-center justify-center px-6 py-16 lg:px-12">
        <div className="w-full max-w-sm">
          <h2 className="font-[var(--font-display)] tracking-[var(--tracking-tight)] text-[var(--text-3xl)]">
            Create your account
          </h2>

          {status === 'sent' ? (
            <div
              role="status"
              aria-live="polite"
              className="mt-8 rounded-[var(--radius-lg)] border border-[var(--color-success)] bg-[var(--color-success-muted)] p-5 text-[var(--color-ink)]"
            >
              <p className="font-medium">Check your email to confirm.</p>
              <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                We sent a confirmation link. Open it to finish setting up your account, then sign
                in.
              </p>
              <p className="mt-4 text-[var(--text-sm)]">
                <Link to="/login" className="text-[var(--color-accent)] hover:underline">
                  Back to sign in
                </Link>
              </p>
            </div>
          ) : (
            <>
              <p className="mt-2 text-[var(--color-ink-muted)] text-[var(--text-base)]">
                Enter the join code your studio sent you, then choose a password.
              </p>
              <form
                onSubmit={(e) => void handleSubmit(onSubmit)(e)}
                noValidate
                className="mt-8 space-y-5"
              >
                <div>
                  <Label htmlFor="invitationCode">Studio join code</Label>
                  <Input
                    id="invitationCode"
                    type="text"
                    inputMode="text"
                    autoCapitalize="characters"
                    autoComplete="one-time-code"
                    placeholder="ABCD1234"
                    required
                    aria-invalid={errors.invitationCode ? 'true' : 'false'}
                    aria-describedby={errors.invitationCode ? 'code-error' : undefined}
                    className="font-[var(--font-mono)] tracking-[0.2em] uppercase"
                    {...register('invitationCode')}
                  />
                  {errors.invitationCode && (
                    <p
                      id="code-error"
                      role="alert"
                      className="mt-2 text-[var(--color-danger)] text-[var(--text-sm)]"
                    >
                      {errors.invitationCode.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    {...register('email')}
                  />
                  {errors.email && (
                    <p
                      id="email-error"
                      role="alert"
                      className="mt-2 text-[var(--color-danger)] text-[var(--text-sm)]"
                    >
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    aria-invalid={errors.password ? 'true' : 'false'}
                    aria-describedby={errors.password ? 'password-error' : undefined}
                    {...register('password')}
                  />
                  {errors.password && (
                    <p
                      id="password-error"
                      role="alert"
                      className="mt-2 text-[var(--color-danger)] text-[var(--text-sm)]"
                    >
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                    aria-describedby={errors.confirmPassword ? 'confirm-error' : undefined}
                    {...register('confirmPassword')}
                  />
                  {errors.confirmPassword && (
                    <p
                      id="confirm-error"
                      role="alert"
                      className="mt-2 text-[var(--color-danger)] text-[var(--text-sm)]"
                    >
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={!isValid || status === 'submitting'}
                  aria-busy={status === 'submitting'}
                  className="w-full"
                >
                  {status === 'submitting' ? 'Creating account…' : 'Create account'}
                </Button>

                {serverError && (
                  <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
                    {serverError}
                  </p>
                )}
              </form>

              <p className="mt-8 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                Already have an account?{' '}
                <Link to="/login" className="text-[var(--color-accent)] hover:underline">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
